/**
 * Item Library Integration for Plot Drives
 * Maps field names to rates from Item Library API
 * Handles dynamic fields where codes come from user selections
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { PLOT_DRIVES_REFERENCES } from './plotDrivesReferenceMapping';
import { PLOT_DRIVES_FIELD_LABELS, PLOT_DRIVES_FIELD_UNITS } from './plotDrivesFieldMapping';
import { PLOT_DRIVES_COLUMN_MAPPING } from './plotDrivesColumnMapping';
import { EVALUATE_CODES, EvaluateCode } from './constants';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * Any sub-base / aggregate item priced per m³ in the item library needs its rate
 * converted to m² for display in the bill summary.  The user enters the layer
 * thickness (in mm) as a dimension value; we read that column and multiply:
 *   effective m² rate = m³ rate × thickness (m)
 *
 * Maps semantic field name → dimensionValues column key that holds the thickness.
 * Covers GWALP006 (MOT Type 1) and GWALP028 (permeable aggregate sub-bases).
 */
export const M3_RATE_THICKNESS_COLUMNS: Record<string, string> = {
  // GWALP006 – MOT Type 1 sub-base
  macadamSubBase: 'col3',
  block300mmAdditionalCapping: 'col9',
  sharedMacadamSubBase: 'col22',
  sharedBlockSubBase: 'col28',
  // GWALP028 – permeable aggregate sub-bases
  permeable10to63mmCleanAggregateSubBase: 'col17',
  permeable5to20mmCleanAggregateSubBase: 'col18',
  sharedPermeable10to63mmCleanAggregateSubBase: 'col36',
  sharedPermeable5to20mmCleanAggregateSubBase: 'col37',
  // Permeable laying aggregate (thickness entered by user)
  permeable2to6mmLayingAggregate: 'col59',
  sharedPermeable2to6mmLayingAggregate: 'col71',
};
export const PLOT_DRIVES_M3_CONVERSION_FIELDS: ReadonlySet<string> = new Set(Object.keys(M3_RATE_THICKNESS_COLUMNS));

function parseThicknessValue(raw: string): number | null {
  // Require the leading digits to be IMMEDIATELY followed by "mm" (optional
  // space) — an unconfigured field's header can fall back to the bare
  // fixed-text tail (e.g. "63-10mm Sub Base - Varied Depths", with no real
  // depth prefix), and an unanchored digit match would misread the "63" from
  // that fixed range text as if it were a configured 63mm thickness.
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)\s*mm\b/i);
  return match ? parseFloat(match[1]) : null;
}

function getM3ToM2Multiplier(fieldName: string, groundworksData: any): number {
  const col = M3_RATE_THICKNESS_COLUMNS[fieldName];
  if (!col) return 1;
  const raw = groundworksData?.plotDrivesColumnHeaders?.[col] as string | undefined;
  if (!raw) return 1;
  const value = parseThicknessValue(raw);
  if (!value || value <= 0) return 1;
  // value >= 1 → entered as mm (e.g. "150") → convert to metres
  // value < 1  → already metres (e.g. "0.150")
  return value >= 1 ? value / 1000 : value;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'm²',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - preserve the normalized code so dev mode can show what code was expected
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'm²',
    resourceCode: normalizedCode,
  };
}

/**
 * Create reverse mapping from label to code for EVALUATE_CODES
 */
function createLabelToCodeMapping(): Record<string, string> {
  const labelToCode: Record<string, string> = {};

  Object.entries(EVALUATE_CODES).forEach(([code, info]) => {
    if (info.label) {
      labelToCode[info.label] = code;
    }
  });

  return labelToCode;
}

const LABEL_TO_CODE = createLabelToCodeMapping();

/**
 * Get evaluate code from label
 * @param labelOrCode - Could be either a label ("100mm thick AC 32mm dense bin") or code ("ALP042")
 */
export function getCodeFromLabelOrCode(labelOrCode: string): { code: string; description: string } | undefined {
  const trimmed = labelOrCode.trim();

  // Check if it's already a code (starts with ALP or GWALP)
  if (trimmed.startsWith('ALP') || trimmed.startsWith('GWALP')) {
    const evaluateCode = trimmed as EvaluateCode;
    if (EVALUATE_CODES[evaluateCode]) {
      return {
        code: trimmed,
        description: EVALUATE_CODES[evaluateCode].label
      };
    }
  }

  // Otherwise, reverse-lookup the code from the label
  const code = LABEL_TO_CODE[trimmed];
  if (code) {
    return {
      code,
      description: trimmed
    };
  }

  return undefined;
}

/**
 * True if a raw Configure Headers dimension value actually resolves to a
 * priceable evaluate code — either a literal ALP/GWALP... code recognised by
 * EVALUATE_CODES, or text that exact-matches a known code's label. Used by
 * groundworksConfigureHeadersValidation.ts's Plot Drives check: for the
 * "varies" dynamic-code fields (DB Base/Binder, surface course, paviors —
 * PLOT_DRIVES_COL_CODES entries that are `{}`), a non-blank value that fails
 * to resolve here is exactly as unpriced as a blank one (getDynamicCodeInfo
 * below would find nothing for it either), so `isBlank()` alone under-reports
 * it — e.g. raw imported header text that doesn't match any code/brand.
 */
export function isResolvablePlotDrivesEvaluateValue(value: string | undefined | null): boolean {
  if (!value) return false;
  return !!getCodeFromLabelOrCode(value);
}

/**
 * Transform plotDrivesDimensionValues from column format to semantic field format
 * @param plotDrivesDimensionValues - Column-based dimension values (e.g., {col4: 'ALP042'} or {col4: '100mm thick...'})
 * @returns Semantic field-based dimension values with code and description
 */
function transformDimensionValuesToSemanticFormat(
  plotDrivesDimensionValues: Record<string, string>
): Record<string, { code: string; description: string }> {
  const semanticDimensions: Record<string, { code: string; description: string }> = {};

  Object.entries(plotDrivesDimensionValues).forEach(([colKey, value]) => {
    const fieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
    if (fieldName && value && typeof value === 'string' && value.trim() !== '') {
      const codeInfo = getCodeFromLabelOrCode(value);
      if (codeInfo) {
        semanticDimensions[fieldName] = codeInfo;
      }
    }
  });

  return semanticDimensions;
}

/**
 * Get dynamic code info for a field from transformed dimension values
 * @param fieldName - Semantic field name (e.g., 'macadamDbBase')
 * @param semanticDimensions - Transformed dimension values in semantic format
 */
function getDynamicCodeInfo(
  fieldName: string,
  semanticDimensions: Record<string, { code: string; description: string }>
): { code: string; description: string } | undefined {
  return semanticDimensions[fieldName];
}

/**
 * Create a rate lookup map from item library for Plot Drives
 * @param itemLibraryData - Item library response from API
 * @param groundworksData - Groundworks data for dynamic field values (optional)
 */
export function createPlotDrivesRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksData?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Transform dimension values from column format to semantic format upfront
  const semanticDimensions = groundworksData?.plotDrivesDimensionValues
    ? transformDimensionValuesToSemanticFormat(groundworksData.plotDrivesDimensionValues)
    : {};

  const overridesByField: Record<string, string> = {};
  Object.entries((groundworksData?.plotDrivesCodeOverrides as Record<string, string> | undefined) ?? {}).forEach(([colKey, code]) => {
    const fieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
    if (fieldName && code) overridesByField[fieldName] = code;
  });

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(PLOT_DRIVES_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = PLOT_DRIVES_FIELD_LABELS[fieldName] || fieldName;

    // Dev-mode code pick (templates API) beats both dynamic and static codes
    const overrideCode = overridesByField[fieldName];
    const dynamicCodeInfo = overrideCode
      ? { code: overrideCode, description: resourceCodeMap.get(overrideCode.trim().toUpperCase())?.resourceDescription || fallbackDescription }
      : getDynamicCodeInfo(fieldName, semanticDimensions);

    if (dynamicCodeInfo) {
      // Use dynamic code and description from user selection
      const rateInfo = getRateByCode(
        dynamicCodeInfo.code,
        resourceCodeMap,
        fieldName,
        dynamicCodeInfo.description
      );
      const m3ToM2Dyn = getM3ToM2Multiplier(fieldName, groundworksData);
      rateMap.set(fieldName, {
        ...rateInfo,
        rate: rateInfo.rate * m3ToM2Dyn,
        unit: fieldName in PLOT_DRIVES_FIELD_UNITS ? PLOT_DRIVES_FIELD_UNITS[fieldName] : rateInfo.unit,
      });
      return;
    }

    if (!reference.code) {
      // Fields without static or dynamic codes
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: PLOT_DRIVES_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);

    const m3ToM2 = getM3ToM2Multiplier(fieldName, groundworksData);

    rateMap.set(fieldName, {
      ...rateInfo,
      rate: rateInfo.rate * m3ToM2,
      unit: fieldName in PLOT_DRIVES_FIELD_UNITS ? PLOT_DRIVES_FIELD_UNITS[fieldName] : rateInfo.unit,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getPlotDrivesRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: PLOT_DRIVES_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Plot Drives summary items
 */
export function applyPlotDrivesRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getPlotDrivesRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
